import React, { useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, InputNumber, Modal, Statistic, Switch, Tag, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import {
  TRIGGER_PICKUP_HUB_DATA_FIX,
  TriggerPickupHubDataFixResponse,
  TriggerPickupHubDataFixVariables,
  PICKUP_HUB_DATA_FIX_SUMMARY,
  PickupHubDataFixSummaryResponse,
} from './graphql/pickupHubDataFixGraphql';

type TriggerResult = TriggerPickupHubDataFixResponse['triggerPickupHubDataFix'];

const PickupHubDataFixPage: React.FC = () => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_WMS]);
  const [form] = Form.useForm();
  const [result, setResult] = useState<TriggerResult | null>(null);
  const [trigger, { loading }] = useMutation<
    TriggerPickupHubDataFixResponse,
    TriggerPickupHubDataFixVariables
  >(TRIGGER_PICKUP_HUB_DATA_FIX);

  const { data: summaryData, loading: summaryLoading, refetch: refetchSummary } = useQuery<PickupHubDataFixSummaryResponse>(
    PICKUP_HUB_DATA_FIX_SUMMARY,
    { fetchPolicy: 'network-only' },
  );

  const summary = summaryData?.pickupHubDataFixSummary;

  const handleTrigger = async () => {
    if (!canOperate) {
      message.error('无操作权限');
      return;
    }
    const values = await form.validateFields();
    const dryRun: boolean = values.dryRun;

    Modal.confirm({
      title: dryRun ? '确认执行预演（dryRun）' : '确认执行正式回写',
      content: dryRun
        ? '仅统计将被订正/跳过的数量，不写库。结果与命中/跳过分布见后端日志。'
        : '将按 order_pickup_hub_fix 清单真正回写 order 表的 pickup_hub_id / received_at_hub（仅当前值仍等于错误目的仓值才改）。请确认数仓清单已灌好。',
      okText: '确认触发',
      cancelText: '取消',
      okButtonProps: dryRun ? undefined : { danger: true },
      onOk: async () => {
        try {
          const { data } = await trigger({
            variables: {
              input: {
                dryRun,
                batchSize: values.batchSize,
                delayMs: values.delayMs,
              },
            },
          });
          const payload = data?.triggerPickupHubDataFix ?? null;
          setResult(payload);
          if (payload?.alreadyRunning) {
            message.warning('已有订正任务在执行中，本次触发被忽略');
          } else {
            message.success('任务已入队，后台异步执行');
          }
        } catch (e: any) {
          message.error(e?.message ?? '触发失败');
        }
      },
    });
  };

  return (
    <Card title="订单揽收仓订正">
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="用途"
        description={
          <div>
            修复历史 bug：中转到件扫描回补时把已揽收订单的 pickup_hub_id 覆盖成目的仓、received_at_hub 覆盖成扫描时间。
            <br />
            前置：数仓已把 (order_id / 错误目的仓值 / 正确揽收仓 / 正确揽收时间) 灌入 <code>order_pickup_hub_fix</code> 表。
            本工具触发后台异步分批、带护栏地回写。建议先 dryRun 预演，确认后再正式执行。
          </div>
        }
      />

      <Card size="small" className="mb-4" title="执行结果统计" extra={<Button size="small" loading={summaryLoading} onClick={() => refetchSummary()}>刷新</Button>}>
        {summary ? (
          <div className="flex gap-8">
            <Statistic title="待处理" value={summary.pendingCount} />
            <Statistic title="已修复" value={summary.fixedCount} valueStyle={{ color: '#3f8600' }} />
            <Statistic title="已跳过" value={summary.skippedCount} valueStyle={{ color: '#cf1322' }} />
          </div>
        ) : (
          <span>加载中...</span>
        )}
      </Card>

      <Form
        form={form}
        layout="inline"
        initialValues={{ dryRun: true, batchSize: 500, delayMs: 100 }}
      >
        <Form.Item name="dryRun" label="预演(dryRun)" valuePropName="checked">
          <Switch checkedChildren="是" unCheckedChildren="否" />
        </Form.Item>
        <Form.Item name="batchSize" label="每批条数" rules={[{ required: true, message: '请输入每批条数' }]}>
          <InputNumber min={1} max={5000} />
        </Form.Item>
        <Form.Item name="delayMs" label="批间延迟(ms)" rules={[{ required: true, message: '请输入批间延迟' }]}>
          <InputNumber min={0} max={10000} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" loading={loading} disabled={!canOperate} onClick={handleTrigger}>
            触发订正
          </Button>
        </Form.Item>
      </Form>

      {result && (
        <Descriptions className="mt-4" bordered size="small" column={1} title="本次触发结果">
          <Descriptions.Item label="是否已有任务在跑">
            {result.alreadyRunning ? <Tag color="orange">是（本次已忽略）</Tag> : <Tag color="green">否</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="任务ID">{result.taskId || '-'}</Descriptions.Item>
          <Descriptions.Item label="预估待处理条数">{result.pendingCount}</Descriptions.Item>
          <Descriptions.Item label="描述">{result.description}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
};

export default PickupHubDataFixPage;
